/** Чистая логика черновика «Управление сообществом» и путей загрузки в Storage. */

export const CHAT_URL_ERROR = "Ссылка на чат должна начинаться с https:// или http://";
export const IG_URL_ERROR = "Проверь ссылку на инстаграм сообщества";
export const TG_URL_ERROR = "Проверь ссылку на телеграм-канал";

/** Мягкая нормализация ссылки: "" → null, без протокола → https://, иначе ошибка. */
export function normalizeUrl(raw: string, errorMessage = "Ссылка должна начинаться с https:// или http://"): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  if (/^[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(v)) return `https://${v}`;
  throw new Error(errorMessage);
}

export type ChatAccess = "open" | "closed";

export type CircleDraft = {
  name: string;
  description: string;
  host_note: string;
  instagram_url: string;
  telegram_url: string;
  chat_access: ChatAccess;
  chat_url: string;
};

export type SavedCircle = {
  name: string;
  description: string | null;
  host_note: string | null;
  instagram_url: string | null;
  telegram_url: string | null;
  chat_access: string | null;
};

export function draftFromCircle(c: SavedCircle, chatUrl: string | null | undefined): CircleDraft {
  return {
    name: c.name ?? "",
    description: c.description ?? "",
    host_note: c.host_note ?? "",
    instagram_url: c.instagram_url ?? "",
    telegram_url: c.telegram_url ?? "",
    chat_access: c.chat_access === "closed" ? "closed" : "open",
    chat_url: chatUrl ?? "",
  };
}

const t = (v: string) => v.trim();

/** Есть ли несохранённые изменения (сравнение по обрезанным значениям). */
export function hasDraftChanges(draft: CircleDraft, saved: CircleDraft): boolean {
  return (Object.keys(draft) as (keyof CircleDraft)[]).some((k) => t(draft[k]) !== t(saved[k]));
}

export type DraftErrors = Partial<Record<"name" | "instagram_url" | "telegram_url" | "chat_url", string>>;

export type CirclePatch = {
  name?: string;
  description?: string | null;
  host_note?: string | null;
  instagram_url?: string | null;
  telegram_url?: string | null;
  chat_access?: ChatAccess;
};

/**
 * Патч circles (только изменённые поля) и действие со ссылкой чата.
 * chat: undefined — не трогать; null — удалить; строка — upsert.
 */
export function buildCirclePatch(
  draft: CircleDraft,
  saved: CircleDraft,
): { ok: true; patch: CirclePatch; chat: string | null | undefined } | { ok: false; errors: DraftErrors } {
  const errors: DraftErrors = {};
  const norm = (k: "instagram_url" | "telegram_url" | "chat_url", msg: string) => {
    try {
      return normalizeUrl(draft[k], msg);
    } catch (e) {
      errors[k] = (e as Error).message;
      return null;
    }
  };
  if (!t(draft.name)) errors.name = "Название не может быть пустым";
  const ig = norm("instagram_url", IG_URL_ERROR);
  const tg = norm("telegram_url", TG_URL_ERROR);
  const chatUrl = draft.chat_access === "open" ? norm("chat_url", CHAT_URL_ERROR) : null;
  if (Object.keys(errors).length) return { ok: false, errors };

  const patch: CirclePatch = {};
  if (t(draft.name) !== t(saved.name)) patch.name = t(draft.name);
  if (t(draft.description) !== t(saved.description)) patch.description = t(draft.description) || null;
  if (t(draft.host_note) !== t(saved.host_note)) patch.host_note = t(draft.host_note) || null;
  if ((ig ?? "") !== t(saved.instagram_url)) patch.instagram_url = ig;
  if ((tg ?? "") !== t(saved.telegram_url)) patch.telegram_url = tg;
  if (draft.chat_access !== saved.chat_access) patch.chat_access = draft.chat_access;

  // В закрытом режиме ссылку не трогаем (как раньше).
  const chat = draft.chat_access === "open" && (chatUrl ?? "") !== t(saved.chat_url) ? chatUrl : undefined;
  return { ok: true, patch, chat };
}

/** Путь в Storage: первая папка — всегда id загружающего (так требует политика бакета). */
export function ownStoragePath(uploaderId: string | null | undefined, ...parts: string[]): string {
  if (!uploaderId) throw new Error("Нужно войти, чтобы загрузить файл");
  return [uploaderId, ...parts.filter(Boolean)].join("/");
}
